
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface StrategyCardHeaderProps {
  name: string;
  minInvestment: number;
  isPro: boolean;
  currency: string;
}

const StrategyCardHeader = ({ name, minInvestment, isPro, currency }: StrategyCardHeaderProps) => {
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  
  // Check if we're on the Phemex landing page
  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);
  
  const handleBadgeClick = (e: React.MouseEvent) => {
    // Prevent the click from propagating to parent elements
    e.stopPropagation();
  };

  return (
    <div className="flex flex-col">
      {/* Header with name */}
      <div className={`p-4 ${
        isPro 
          ? "bg-gradient-to-r from-[#407096] to-[#012846] text-white" 
          : "bg-[#607D9B] text-white"
      }`}>
        <h3 className="text-xl font-semibold min-h-[28px]">{name}</h3>
      </div>
      
      {/* Investment amount and badge row */}
      <div className={`flex justify-between items-center p-3 ${isPhemexLanding ? 'bg-gray-900 border-b border-gray-700' : 'bg-white border-b border-gray-200'}`}>
        <div className="flex flex-col">
          <span className={`text-xl font-bold ${isPhemexLanding ? 'text-white' : 'text-[#0F1B2C]'}`}>{minInvestment.toLocaleString()} {currency}</span>
          <span className={`text-xs ${isPhemexLanding ? 'text-gray-300' : 'text-gray-500'}`}>Min. Investment</span>
        </div>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div onClick={handleBadgeClick}>
                <Link to="/strategies" onClick={handleBadgeClick}>
                  {isPro ? (
                    isPhemexLanding ? (
                      <span className="bg-[#F6C528] px-3 py-1 rounded-full text-[#012846] font-semibold text-sm">
                        PREMIUM
                      </span>
                    ) : (
                      <img 
                        src="/lovable-uploads/fec2d69a-b8ba-4895-912d-ccfc126e394d.png" 
                        alt="PREMIUM" 
                        className="h-6" 
                      />
                    )
                  ) : (
                    <span className="bg-[#607D9B] px-3 py-1 rounded-full text-white font-semibold text-sm">FREE</span>
                  )}
                </Link>
              </div>
            </TooltipTrigger>
            <TooltipContent 
              className={`${
                isPro 
                  ? "bg-[#012846] text-white" 
                  : "bg-[#0F1B2C] text-white"
              } p-3 rounded-lg border ${
                isPro ? "border-[#F6C528]" : "border-[#2A3F59]"
              } shadow-lg max-w-[200px]`}
              side="right"
              sideOffset={5}
            >
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">
                  {isPro ? "Premium Strategy" : "Free Strategy"}
                </p>
                <p className="text-xs text-gray-300">
                  {isPro ? "Please click here to learn more" : "Start trading with this strategy for free"}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default StrategyCardHeader;
